import { Controller } from "@hotwired/stimulus"
import { post } from "../lib/http"
import { burstConfetti } from "../lib/confetti"

// Discovery feed card. Tapping "Reveal" flips the card to its revealed look right
// in the feed (both photos, with a reveal animation) and marks the current user
// revealed server-side — no navigation. "View match" then opens the detail page.
export default class extends Controller {
  static targets = ["unrevealed", "revealed", "revealButton", "revealHint", "viewMatch", "revealBadge"]
  static values = { revealUrl: String }

  reveal() {
    if (this.hasUnrevealedTarget) this.unrevealedTarget.style.display = "none"
    if (this.hasRevealedTarget) {
      this.revealedTarget.style.display = "block"
      this.revealedTarget.classList.add("card-reveal-anim")
    }
    this.hideRevealBadges()
    this.ensureMyPhotoUploaded()
    if (this.revealUrlValue) post(this.revealUrlValue).catch(() => {})
  }

  // "Reveal my photo" = approve my photo so the other person can see it (their
  // photo stays blurred to me until THEY approve). Posts the reveal, then replaces
  // the reveal button + hint with the "View match" button in place.
  async approve() {
    if (this.hasRevealButtonTarget) this.revealButtonTarget.style.display = "none"
    if (this.hasRevealHintTarget) this.revealHintTarget.style.display = "none"
    if (this.hasViewMatchTarget) this.viewMatchTarget.style.display = "block"
    this.hideRevealBadges()
    this.ensureMyPhotoUploaded()

    if (!this.revealUrlValue) return
    try {
      const response = await post(this.revealUrlValue)
      if (!response.ok) return
      const result = await response.json()
      if (result.fully_revealed && result.discovery_id) {
        document.querySelectorAll(
          `#discovery-like-${result.discovery_id}[data-like-ready-after-my-reveal]`
        ).forEach((control) => {
          control.hidden = false
          control.removeAttribute("data-like-ready-after-my-reveal")
        })
      }
      // The server is authoritative. A stale cached button can briefly survive a
      // back swipe, but an idempotent second tap must not celebrate twice.
      if (result.revealed) burstConfetti(this.element)
    } catch (error) {}
  }

  // On reveal, make sure my CLEAR photo is actually on S3 now. The group sees a
  // revealed photo via the match-poll, which needs the real image (not just the
  // blur) — and the background prep uploads the blur first and the full image only
  // best-effort, so it may not be up yet (→ the other members see "developing…").
  // Nudge photo-fetch to upload it now. No-op if it's already on S3 (then my own
  // cell shows the image and carries no data-client-ref).
  ensureMyPhotoUploaded() {
    const refs = Array.from(this.element.querySelectorAll("[data-client-ref]"))
      .map((el) => el.dataset.clientRef)
      .filter(Boolean)
    if (refs.length === 0) return
    document.dispatchEvent(new CustomEvent("photo-fetch:upload-now", { detail: { clientRefs: refs } }))
  }

  // Drop the "Not revealed yet" badge on my own photo the moment I reveal (both
  // entry points post the reveal). Works for 1:1 and groups — the group cell's
  // own-photo badge is a revealBadge target too (other members' copies un-blur via
  // the match-poll). No-op where there's no badge.
  hideRevealBadges() {
    this.revealBadgeTargets.forEach((el) => { el.style.display = "none" })
  }
}
